import { useRef } from 'react';
import useDarkMode from '../../../../hooks/useDarkMode';
import BudgetCurrencyInput from './BudgetCurrencyInput';
import CoverageBadge from './CoverageBadge';
import { IBudgetItem } from './BudgetItemsTable';
import BudgetDescriptionSelect from './BudgetDescriptionSelect';
import BudgetPercentageInput from './BudgetPercentageInput';
import BudgetQuantityInput from './BudgetQuantityInput';
import { IBudgetServiceItemOption } from '../../../../api/fetchBudget';
import { parseDiscountPercent, parseQuantityToNumber } from '../utils/budgetCalculations';

interface IBudgetTableRowProps {
  item: IBudgetItem;
  originalItem?: IBudgetItem;
  index: number;
  isExternalService?: boolean;
  isEditMode?: boolean;
  onItemChange?: (index: number, field: keyof IBudgetItem, value: string | number) => void;
  onRemoveItem?: (index: number) => void;
}

const BudgetTableRow = (props: IBudgetTableRowProps) => {
  const isRemoved = props.item.removed === true;
  const originalItem = props.originalItem;
  const valueInputRef = useRef<HTMLInputElement>(null);
  const isDarkMode = useDarkMode();
  const removedBackgroundColor = isDarkMode ? '#3a1f20' : '#fef2f2';

  const currentQuantity = String(props.item.quantity || '').trim();
  const originalQuantityStr = originalItem ? String(originalItem.quantity || '').trim() : '';
  const quantityChanged = originalItem && originalQuantityStr !== '' && originalQuantityStr !== currentQuantity;
  const originalQuantity = originalItem?.quantity;

  const isQuantityZero = parseQuantityToNumber(currentQuantity) === 0;
  const shouldShowRemovedStyle = isRemoved || isQuantityZero;

  const currentValue = typeof props.item.value === 'number' ? props.item.value : parseFloat(String(props.item.value || 0)) || 0;
  const originalValueNum = originalItem 
    ? (typeof originalItem.value === 'number' ? originalItem.value : parseFloat(String(originalItem.value || 0)) || 0)
    : 0;
  const valueChanged = originalItem && originalValueNum !== 0 && Math.abs(originalValueNum - currentValue) > 0.01;
  const originalValue = originalItem?.value;

  const formatOriginalValue = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    const reais = Math.floor(value);
    const centavos = Math.round((value - reais) * 100);
    const formattedReais = reais.toLocaleString('pt-BR');
    const formattedCentavos = String(centavos).padStart(2, '0');
    return `R$ ${formattedReais},${formattedCentavos}`;
  };

  const handleDescriptionChange = (option: { id: string; label: string; value?: number; itemData?: IBudgetServiceItemOption } | undefined) => {
    if (!isRemoved && !isQuantityZero && option) {
      const description = option.id || option.label.split(' (')[0] || '';
      props.onItemChange?.(props.index, 'description', description);

      if (option.itemData) {
        const itemData = option.itemData;
        const hasValue = option.value && option.value !== 0;
        const hasDiscount = props.item.discount && props.item.discount !== '0%' && props.item.discount !== '00%';

        if (itemData.item) {
          props.onItemChange?.(props.index, 'item', itemData.item);
        }

        if (itemData.quantity !== undefined && itemData.quantity !== null && itemData.quantity > 0) {
          const quantityStr = String(itemData.quantity).padStart(2, '0');
          props.onItemChange?.(props.index, 'quantity', quantityStr);
        } else if (itemData.quantity === 0 || itemData.quantity === null) {
          props.onItemChange?.(props.index, 'quantity', '01');
        }
        
        if (hasValue || hasDiscount) {
          props.onItemChange?.(props.index, 'value', option.value || 0);
          props.onItemChange?.(props.index, 'discount', '00%');
          
          setTimeout(() => {
            if (valueInputRef.current) {
              valueInputRef.current.focus();
            }
          }, 100);
        }
      }
    }
  };

  const hasChanges = quantityChanged || valueChanged;
  const isCovered = props.item.isCovered === true;
  const notCoveredReason = props.item.notCoveredReason;

  return (
    <tr
      className={`border-0 !border-b-0 ${shouldShowRemovedStyle ? 'h-[84px]' : 'h-[50px]'}`}
      style={{ 
        borderBottom: 'none'
      }}
    >
      <td className="border-0 !border-b-0 " style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        {props.item.ref}
      </td>
      <td className="border-0 !border-b-0 relative" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0', overflow: 'visible' }}>
        {shouldShowRemovedStyle && (
          <div className="text-red-400 text-size-14 font-bold px-2 absolute bottom-[-0px] left-0 pl-5 whitespace-nowrap z-10">
            Removido
          </div>
        )}
        <input
          type="text"
          className="form-control"
          value={props.item.item || ''}
          onChange={(e) => {
            if (!isRemoved && !isQuantityZero) {
              props.onItemChange?.(props.index, 'item', e.target.value);
            }
          }}
          disabled={shouldShowRemovedStyle || isCovered}
          style={shouldShowRemovedStyle ? { backgroundColor: removedBackgroundColor } : undefined}
          autoFocus={(!props.item.item || props.item.item.trim() === '') && !shouldShowRemovedStyle}
        />
      </td>
      <td className="border-0 !border-b-0 relative" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0', overflow: 'visible' }}>
        {props.isExternalService ? (
          <input
            type="text"
            className="form-control"
            value={props.item.description || ''}
            onChange={(e) => {
              if (!isRemoved && !isQuantityZero) {
                props.onItemChange?.(props.index, 'description', e.target.value);
              }
            }}
            disabled={shouldShowRemovedStyle || isCovered}
            style={shouldShowRemovedStyle ? { backgroundColor: removedBackgroundColor } : undefined}
            placeholder="Descrição"
          />
        ) : (
          <BudgetDescriptionSelect
            value={props.item.description || ''}
            onChange={handleDescriptionChange}
            disabled={shouldShowRemovedStyle || isCovered}
            isRemoved={shouldShowRemovedStyle}
            autoFocus={false}
          />
        )}
      </td>
      <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        <div className="relative">
          <BudgetQuantityInput
            value={shouldShowRemovedStyle && originalQuantity ? originalQuantity : props.item.quantity}
            onChange={(value) => {
              if (!isRemoved && !isQuantityZero) {
                props.onItemChange?.(props.index, 'quantity', value);
              }
            }}
            disabled={shouldShowRemovedStyle || isCovered}
            isRemoved={shouldShowRemovedStyle}
            isChanged={quantityChanged && !shouldShowRemovedStyle}
          />
          {quantityChanged && originalQuantity && !shouldShowRemovedStyle && (
            <span className="absolute font-bold text-size-12 text-gray-500 dark:text-neutral-400 top-full left-0 mt-0.5 whitespace-nowrap">Anterior ({originalQuantity})</span>
          )}
        </div>
      </td>
      <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        <div className="relative">
          <BudgetCurrencyInput
            ref={valueInputRef}
            value={props.item.value}
            onChange={(value) => {
              if (!isRemoved && !isQuantityZero) {
                props.onItemChange?.(props.index, 'value', value);
              }
            }}
            disabled={shouldShowRemovedStyle || isCovered}
            isRemoved={shouldShowRemovedStyle}
            isChanged={valueChanged}
          />
          {valueChanged && originalValue !== undefined && (
            <span className="absolute font-bold text-size-12 text-gray-500 dark:text-neutral-400 top-full left-0 mt-0.5 whitespace-nowrap">Anterior ({formatOriginalValue(originalValue)})</span>
          )}
        </div>
      </td>
      <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        <CoverageBadge isCovered={isCovered} notCoveredReason={notCoveredReason} />
      </td>
      <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        <BudgetPercentageInput
          value={props.item.discount}
          onChange={(value) => {
            if (!isRemoved && !isQuantityZero && !isCovered) {
              const discountValue =
                value % 1 === 0
                  ? `${String(value).padStart(2, '0')}%`
                  : `${value.toFixed(2).replace('.', ',')}%`;
              props.onItemChange?.(props.index, 'discount', discountValue);
            }
          }}
          disabled={shouldShowRemovedStyle || isCovered}
          isRemoved={shouldShowRemovedStyle}
        />
      </td>
      <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        <BudgetCurrencyInput
          value={isCovered ? 0 : parseQuantityToNumber(props.item.quantity) * props.item.value * (1 - parseDiscountPercent(props.item.discount) / 100)}
          onChange={(value) => props.onItemChange?.(props.index, 'subtotal', value)}
          disabled={true}
          isRemoved={shouldShowRemovedStyle}
        />
      </td>
      <td className="text-center border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: hasChanges ? '25px' : '0' }}>
        {!isCovered && <button
          type="button"
          className={`cursor-pointer bg-transparent border-none p-0 ${
            shouldShowRemovedStyle ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={() => {
            if (!isRemoved && !isQuantityZero) {
              props.onRemoveItem?.(props.index);
            }
          }}
          disabled={shouldShowRemovedStyle}
        >
          <img src="/assets/images/bin.svg" alt="Excluir item" className="w-6 h-6" />
        </button>}
      </td>
    </tr>
  );
};

export default BudgetTableRow;
